import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Package } from 'lucide-react';
import api from '../lib/api';
import { toast, Toaster } from 'sonner';

export default function ConfirmOrder() {
    const { token } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/confirm/${token}`);
                setOrder(response.data);
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.detail || 'Lien invalide ou expiré');
            } finally {
                setLoading(false);
            }
        };
        
        if (token) {
            fetchOrder();
        }
    }, [token]);

    const handleConfirm = async () => {
        try {
            setConfirming(true);
            const response = await api.post(`/confirm/${token}`, {});
            setSuccess('confirmed');
            toast.success(response.data.message);
            setOrder(prev => ({ ...prev, confirmation_status: 'confirmed' }));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la confirmation');
        } finally {
            setConfirming(false);
        }
    };

    const handleCancel = async () => {
        try {
            setCancelling(true);
            const response = await api.post(`/confirm/${token}/cancel`);
            setSuccess('cancelled');
            toast.success(response.data.message);
            setOrder(prev => ({ ...prev, confirmation_status: 'cancelled' }));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'annulation');
        } finally {
            setCancelling(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Loader2 className="w-16 h-16 animate-spin text-amber-600 mb-4" />
                        <p className="text-lg text-gray-600">Chargement...</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
                <Card className="w-full max-w-md border-red-200">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <XCircle className="w-20 h-20 text-red-500 mb-4" />
                        <h2 className="text-2xl font-bold text-red-700 mb-2">Lien invalide</h2>
                        <p className="text-gray-600 text-center">{error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Already confirmed
    if (order?.confirmation_status === 'confirmed' || success === 'confirmed') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
                <Toaster position="top-center" richColors />
                <Card className="w-full max-w-md border-green-300 shadow-xl">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <CheckCircle className="w-24 h-24 text-green-500 mb-6" />
                        <h2 className="text-3xl font-bold text-green-700 mb-2">Merci !</h2>
                        <p className="text-xl text-gray-600 text-center mb-4">
                            Votre commande est confirmée
                        </p>
                        <div className="bg-green-100 px-6 py-3 rounded-full">
                            <span className="text-green-700 font-bold text-lg">{order?.n_commande}</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Already cancelled
    if (order?.confirmation_status === 'cancelled' || success === 'cancelled') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white flex items-center justify-center p-4">
                <Toaster position="top-center" richColors />
                <Card className="w-full max-w-md border-gray-300 shadow-xl">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <XCircle className="w-24 h-24 text-gray-400 mb-6" />
                        <h2 className="text-3xl font-bold text-gray-600 mb-2">Commande annulée</h2>
                        <p className="text-lg text-gray-500 text-center">
                            {order?.n_commande}
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Main confirmation view - SIMPLE & CLEAR
    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
            <Toaster position="top-center" richColors />
            
            <Card className="w-full max-w-md shadow-2xl border-amber-200">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 rounded-t-lg text-center">
                    <Package className="w-12 h-12 mx-auto mb-2" />
                    <h1 className="text-2xl font-bold">BBM</h1>
                </div>
                
                <CardContent className="p-6">
                    {/* Order Summary */}
                    <div className="text-center mb-8">
                        <p className="text-gray-500 text-sm mb-1">Commande</p>
                        <p className="text-2xl font-bold text-amber-600 mb-4">{order?.n_commande}</p>
                        
                        <div className="bg-gray-50 rounded-xl p-4 mb-4">
                            <p className="text-gray-600 text-sm mb-1">Produit</p>
                            <p className="font-semibold text-lg">{order?.produit}</p>
                        </div>
                        
                        <div className="bg-green-50 rounded-xl p-4">
                            <p className="text-gray-600 text-sm mb-1">Total à payer</p>
                            <p className="text-3xl font-bold text-green-600">
                                {order?.prix_total?.toLocaleString()} DA
                            </p>
                        </div>
                    </div>
                    
                    {/* Client Info */}
                    <div className="bg-blue-50 rounded-xl p-4 mb-8 text-sm">
                        <p><strong>👤</strong> {order?.full_name}</p>
                        <p><strong>📞</strong> {order?.phone}</p>
                        <p><strong>📍</strong> {order?.adresse}</p>
                    </div>
                    
                    {/* BIG ACTION BUTTONS */}
                    <div className="space-y-4">
                        <Button 
                            className="w-full h-16 text-xl font-bold bg-green-500 hover:bg-green-600 rounded-xl shadow-lg"
                            onClick={handleConfirm}
                            disabled={confirming || cancelling}
                        >
                            {confirming ? (
                                <Loader2 className="w-6 h-6 animate-spin" />
                            ) : (
                                <>✅ CONFIRMER</>
                            )}
                        </Button>
                        
                        <Button 
                            variant="outline"
                            className="w-full h-14 text-lg font-semibold text-red-600 border-2 border-red-300 hover:bg-red-50 rounded-xl"
                            onClick={handleCancel}
                            disabled={confirming || cancelling}
                        >
                            {cancelling ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>❌ ANNULER</>
                            )}
                        </Button>
                    </div>
                    
                    <p className="text-xs text-center text-gray-400 mt-6">
                        Paiement à la livraison
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
